import type {FC} from 'react';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Roles} from '@wellstone-solutions/common';
import {
  Box,
  Button,
  Icon,
  IconNames,
  Stack,
  Typography,
} from '@wellstone-solutions/web';
import {useStores} from 'hooks/useStores';
import {showAlert, ALERT_TYPES} from 'utils/showAlert';
import {MEMBER_ROLES} from 'constants/Members';
import type {Channel} from '../types';

interface ConversationHeaderProps {
  channel: Channel;
}

export const ConversationHeader: FC<ConversationHeaderProps> = ({channel}) => {
  const {memberStore} = useStores();
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false);
  const [channelMember] = channel.members;

  if (!channelMember) {
    return null;
  }

  const handleViewProfile = async () => {
    setIsNavigating(true);
    try {
      const member = await memberStore.getMemberForUserId(channelMember.id);

      if (member?.id) {
        navigate(
          channelMember.role === Roles.PATIENT
            ? `/members/${member.id}`
            : `/staff/${member.id}`,
        );
      } else {
        showAlert(
          'Unable to open the profile. Please try again.',
          ALERT_TYPES.ERROR,
        );
      }
    } catch (error) {
      showAlert(
        'Unable to open the profile. Please try again.',
        ALERT_TYPES.ERROR,
      );
      console.error(error);
    } finally {
      setIsNavigating(false);
    }
  };

  return (
    <Stack
      data-testid="conversation-header"
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{marginBottom: 2}}>
      <Box>
        <Typography sx={{fontSize: 16, fontWeight: 700, lineHeight: 1.5}}>
          {channelMember.name}
        </Typography>
        {channelMember.role in MEMBER_ROLES && (
          <Typography variant="caption" color="text.secondary">
            {MEMBER_ROLES[channelMember.role]}
          </Typography>
        )}
      </Box>
      <Button
        data-testid="view-profile-button"
        size="small"
        disabled={isNavigating}
        onClick={handleViewProfile}
        startIcon={<Icon name={IconNames.User} size={18} />}>
        View profile
      </Button>
    </Stack>
  );
};
